import { DateTime } from 'luxon';
import { Netmask } from 'netmask';
import { defineStore } from 'pinia';
import { computed, readonly, ref } from 'vue';

import measureRegionLatency from '@/helpers/measure_region_latency';
import nateFetch from '@/helpers/nate_fetch';
import resolveConnectionMode from '@/helpers/resolve_connection_mode';
import useSettingsStore from '@/stores/settings';

const VPN_TIMEOUT_MS = 4000;
const LATEST_HANDSHAKE_ONLINE_THRESHOLD_SECONDS = 5 * 60;

// Per-region base URLs: raw in-VPN gateways (direct mode) vs public proxies (proxy mode).
const DIRECT_BASE_URLS = {
  EU: import.meta.env.VITE_NATE_EU_DIRECT_URL || 'http://10.212.212.1',
  US: import.meta.env.VITE_NATE_US_DIRECT_URL || 'http://10.213.213.1',
};

const PROXY_BASE_URLS = {
  EU: import.meta.env.VITE_NATE_EU_PROXY_URL || 'https://proxy.eu.robocam.slideslive.dev',
  US: import.meta.env.VITE_NATE_US_PROXY_URL || 'https://proxy.us.robocam.slideslive.dev',
};

const useVpnStore = defineStore('vpn', () => {
  const settingsStore = useSettingsStore();

  const region = ref(undefined);
  const clients = ref([]);
  const latencies = ref({ EU: null, US: null });

  const mode = computed(() => resolveConnectionMode(settingsStore.connectionMode));
  const regionBaseUrls = computed(() => (mode.value === 'proxy' ? PROXY_BASE_URLS : DIRECT_BASE_URLS));

  // Base URL of the selected region; per-station URLs are built from it.
  const activeBaseUrl = computed(() => (region.value ? regionBaseUrls.value[region.value] : null));

  const connected = computed(() => (region.value === undefined ? undefined : !!region.value));
  const onlineClients = computed(() => {
    return clients.value.filter((client) => {
      if (!client.latestHandshakeAt) return false;

      const latestHandshakeAt = DateTime.fromISO(client.latestHandshakeAt);
      return latestHandshakeAt >= DateTime.now().minus({ seconds: LATEST_HANDSHAKE_ONLINE_THRESHOLD_SECONDS });
    });
  });

  const clientsEndpoint = computed(() => (activeBaseUrl.value ? `${activeBaseUrl.value}/vpn/clients` : null));

  function stationOnline({ host }) {
    return !!onlineClients.value.find((client) => {
      const networks =
        client.serverAllowedIPs?.map((address) => {
          return new Netmask(address);
        }) ?? [];

      return !!networks.find((network) => network.contains(host));
    });
  }

  // Direct mode: only the local VPN's gateway answers, so first-to-respond wins (reachability, not latency).
  async function selectRegionDirect() {
    const bases = regionBaseUrls.value;

    return Promise.any([
      nateFetch(bases.EU).then(() => 'EU'),
      nateFetch(bases.US).then(() => 'US'),
      new Promise((resolve) => setTimeout(() => resolve(null), VPN_TIMEOUT_MS)),
    ]);
  }

  // Proxy mode: pick the fastest region (or a forced EU/US preference). Measured once then pinned —
  // incidental refreshes reuse the choice so it can't flap; pass remeasure to re-test.
  async function selectRegionProxy({ remeasure }) {
    if (region.value && !remeasure) return region.value;

    const measured = await measureRegionLatency(regionBaseUrls.value);
    latencies.value = measured;

    const preference = settingsStore.regionPreference;
    if ((preference === 'EU' || preference === 'US') && Number.isFinite(measured[preference])) {
      return preference;
    }

    const best =
      !Number.isFinite(measured.EU) && !Number.isFinite(measured.US) ? null : measured.EU <= measured.US ? 'EU' : 'US';

    // Hysteresis: don't drop a working region because a re-measure momentarily found nothing.
    if (best === null && region.value) return region.value;
    return best;
  }

  async function refresh({ remeasure = false } = {}) {
    region.value = mode.value === 'proxy' ? await selectRegionProxy({ remeasure }) : await selectRegionDirect();

    await refreshClients();

    return region.value;
  }

  async function refreshClients() {
    if (!clientsEndpoint.value) {
      clients.value = [];
      return;
    }

    // On failure keep the previous snapshot: wiping it would flip every station to
    // offline and fire a refresh storm from the online-transition watcher on recovery.
    let data;
    try {
      const response = await nateFetch(clientsEndpoint.value, { signal: AbortSignal.timeout(VPN_TIMEOUT_MS) });
      data = await response.json();
    } catch (error) {
      console.warn('VPN clients refresh failed:', error);
      return;
    }

    if (!data.success) {
      console.warn('VPN clients refresh failed:', data);
      return;
    }

    clients.value = data.clients;
  }

  return {
    region: readonly(region),
    mode,
    latencies: readonly(latencies),
    activeBaseUrl,
    directBaseUrls: DIRECT_BASE_URLS,

    connected,
    onlineClients,

    stationOnline,
    refresh,
    refreshClients,
  };
});

export default useVpnStore;
